import React, { memo, useState } from "react";
import isEmpty from "lodash/isEmpty";
import uniqBy from "lodash/uniqBy";
import Link from "next/link";

import getSearchResults from "@/api/requests/getSearchResults";
import { WEIGHTS } from "@/constants/strains";
import { Strain } from "@/custom-types/Strain";
import useDomainCountryCode from "@/hooks/useDomainCountryCode";
import useOnComponentInView from "@/hooks/useOnComponentInView";
import { useSelector } from "@/redux/hooks";
import { getCoordinates } from "@/redux/selectors/location";

import {
  StrainCardPlaceholder,
  StrainCardWithMenuItems,
} from "@/components/botanic/StrainCard";

const HighThcStrains = () => {
  const [strains, setStrains] = useState([{}, {}, {}]);

  const coordinates = useSelector(getCoordinates);
  const countryCode = useDomainCountryCode();

  const [ref] = useOnComponentInView(
    () => {
      const fetchStrains = async (
        filter?: Record<string, string | string[]>,
      ) => {
        const { strain } = await getSearchResults(
          {
            boost: "eighth_oz",
            "filter[thc_minimum]": "20",
            "filter[type]": "strain",
            ...filter,
          },
          coordinates,
          20, // max distance, but not used as of 2021-03-31
          countryCode,
          3,
          0,
          "",
          true,
        );

        return strain;
      };

      const getStrains = async () => {
        const normalizedQuantities = WEIGHTS.map((weight) => {
          return weight.value;
        });

        const strainsData = await fetchStrains({
          "filter[max_distance]": "30",
          "filter[normalized_quantity]": normalizedQuantities,
          "filter[product_category]": "Flower",
          "filter[unit]": "g",
        });

        if (strainsData.length < 3) {
          const refetchedStrainsData = await fetchStrains();
          if (refetchedStrainsData.length) {
            const combineStrains = strains.concat(refetchedStrainsData);
            setStrains(uniqBy(combineStrains.slice(0, 3), "id"));
          }
        } else {
          setStrains(strainsData);
        }
      };

      getStrains();
    },
    {
      triggerOnce: true,
    },
  );

  return (
    <div ref={ref}>
      <div className="container py-lg">
        <h2 className="text-md mb-md">High THC weed strains nearby</h2>
        <div className="row">
          {strains?.length &&
            strains.map((strain, i) => (
              <div className="md:col-1/3 mb-md md:mb-none" key={i}>
                {Object.entries(strain).length === 0 ? (
                  <StrainCardPlaceholder
                    className="h-full shadow-low rounded bg-white"
                    comparePricesView={true}
                    orientation="horizontal"
                  />
                ) : (
                  <StrainCardWithMenuItems
                    strain={strain as Strain}
                    trackingLabel="Compare Prices"
                    orientation="horizontal"
                    preserveHeight={true}
                  />
                )}
              </div>
            ))}
          {isEmpty(strains) && (
            <div className="col">An error occurred while fetching strains.</div>
          )}
        </div>

        <p className="text-green underline text-left text-sm mt-md md:mt-xl">
          <Link href="/strains/lists/curated/high-thc-weed-strains">
            see all strains with high THC
          </Link>
        </p>
      </div>
    </div>
  );
};

export default memo(HighThcStrains);
